// HUD
export const HUD = {
  POWER_BAR_WIDTH: 60,
  POWER_BAR_HEIGHT: 8,
  POWER_BAR_OFFSET_Y: -55,
  // Power color thresholds
  POWER_COLOR_NORMAL: 0xffff00,   // yellow
  POWER_COLOR_HIGH: 0xff8800,     // orange (>75%)
  BAR_BORDER_COLOR: 0x333333,
  // Damage % text (replaces health bars)
  DAMAGE_TEXT_OFFSET_Y: -45,
  DAMAGE_FONT_SIZE: 16,
  DAMAGE_COLOR_LOW: 0x00ff00,       // green (0-50%)
  DAMAGE_COLOR_MED: 0xffdd00,       // gold (50-100%)
  DAMAGE_COLOR_HIGH: 0xff8800,      // orange (100-150%)
  DAMAGE_COLOR_CRITICAL: 0xff0044,  // red (150%+)
} as const;

// HUD layout
export const HUD_LAYOUT = {
  TITLE_X: 20,
  TITLE_Y: 25,
  TITLE_FONT_SIZE: 20,
  ROUND_Y: 25,
  ROUND_FONT_SIZE: 14,
  STATS_Y: 55,
  STATS_FONT_SIZE: 14,
  HINTS_Y_OFFSET: 25,   // from canvas bottom
  HINTS_FONT_SIZE: 12,
  NAME_LABEL_OFFSET_Y: -65,
  NAME_LABEL_FONT_SIZE: 12,
  TURN_FONT_SIZE: 28,
  GAME_OVER_FONT_SIZE: 48,
  FPS_FONT_SIZE: 14,
  WIND_FONT_SIZE: 22,
  ELIMINATED_FONT_SIZE: 24,
  CONSUMED_FONT_SIZE: 28,
  LAUNCHED_FONT_SIZE: 28,
} as const;

// Aim acceleration — forgiving start, faster the longer you hold
export const AIM = {
  MIN_SPEED: 0.015,       // radians/frame at start — responsive start
  MAX_SPEED: 0.06,        // radians/frame at full ramp (~3.4°) — faster than old constant
  RAMP_FRAMES: 25,        // frames to reach max speed (~0.42s at 60fps)
  // Full 360° rotation — no dead zone
  MIN_ANGLE: -Math.PI / 2,             // straight down (right side of wrap)
  MAX_ANGLE: Math.PI * 3 / 2,          // straight down (left side of wrap = 3π/2)
} as const;

// Round start cinematic sequence
export const ROUND_START = {
  DURATION: 1.5,              // total sequence length (seconds)
  PAN_TO_PLAYER_AT: 0.6,     // seconds into sequence when camera shifts to player
  FADE_OUT_AT: 0.3,           // seconds remaining when text starts fading
  TEXT_FONT_SIZE: 48,
  TEXT_ENTER_DURATION: 0.2,   // scale-punch entrance time (seconds)
  TEXT_ENTER_SCALE: 1.3,      // initial overshoot scale
} as const;

// Game over
export const GAME_OVER = {
  DISPLAY_DURATION: 3.0, // seconds (legacy — now only used for solo mode auto-reset)
} as const;

// Post-match summary
export const POST_MATCH = {
  // Entrance animation timing (seconds)
  OVERLAY_FADE_DURATION: 0.2,
  TITLE_PUNCH_DELAY: 0.2,
  TITLE_PUNCH_DURATION: 0.2,
  CARDS_SLIDE_DELAY: 0.5,
  CARDS_SLIDE_DURATION: 0.2,
  AWARDS_FADE_DELAY: 0.8,
  AWARDS_STAGGER: 0.1,
  BUTTONS_FADE_DELAY: 1.2,
  READY_AT: 1.5,            // seconds until buttons become interactive
  // Layout
  OVERLAY_ALPHA: 0.85,
  TITLE_Y: 120,
  TITLE_FONT_SIZE: 48,
  SUBTITLE_Y: 180,
  SUBTITLE_FONT_SIZE: 20,
  // Stat cards
  CARD_WIDTH: 280,
  CARD_HEIGHT: 200,
  CARD_GAP: 80,
  CARD_Y: 240,
  CARD_CORNER_RADIUS: 12,
  CARD_BG_ALPHA: 0.7,
  CARD_BORDER_WIDTH: 2,
  STAT_FONT_SIZE: 14,
  STAT_VALUE_FONT_SIZE: 16,
  TANK_NAME_FONT_SIZE: 20,
  // Awards
  AWARDS_Y: 520,
  AWARD_ROW_HEIGHT: 36,
  AWARD_FONT_SIZE: 14,
  AWARD_ICON_SIZE: 16,
  // Buttons
  BUTTON_Y: 680,
  BUTTON_WIDTH: 200,
  BUTTON_HEIGHT: 52,
  BUTTON_GAP: 40,
  BUTTON_FONT_SIZE: 18,
  BUTTON_CORNER_RADIUS: 8,
  // Colors
  STAT_LABEL_COLOR: 0x888888,
  STAT_VALUE_COLOR: 0xffffff,
  AWARD_COLOR: 0xffd700,
} as const;

// Turn flow — confirmation dialog + turn timer
export const TURN_FLOW = {
  TURN_DELAY: 0.8,              // seconds between turns
  PHASE_CHANGE_FREEZE_MS: 150,  // brief freeze on turn start (prevent held-key input)
  // Dialog
  DIALOG_WIDTH: 400,
  DIALOG_HEIGHT: 130,
  DIALOG_BG_ALPHA: 0.6,
  DIALOG_BG_COLOR: 0x050510,
  DIALOG_BORDER_WIDTH: 2,
  DIALOG_BORDER_GLOW_DIST: 20,
  DIALOG_BORDER_GLOW_STRENGTH: 2.5,
  DIALOG_PULSE_SPEED: 5,       // sin(time * speed) for "GET READY" alpha
  DIALOG_PULSE_MIN: 0.3,
  DIALOG_PULSE_MAX: 0.9,
  DIALOG_AUTO_DURATION: 1.0,   // seconds dialog is visible before auto-transition
  DIALOG_ENTER_DURATION: 0.2,  // seconds for scale-punch entrance animation
  DIALOG_ENTER_SCALE: 1.3,     // initial overshoot scale on appear
  // Turn timer
  TURN_DURATION: 45,            // seconds (hidden backstop — momentum is the real turn limit)
  TIMER_WARN_THRESHOLD: 10,     // yellow below this
  TIMER_CRITICAL_THRESHOLD: 5,  // red + pulsing below this
  TIMER_PULSE_SPEED: 8,         // faster pulse when critical
  TIMER_FONT_SIZE: 28,
  TIMER_COLOR_NORMAL: 0xffffff,
  TIMER_COLOR_WARN: 0xffff00,
  TIMER_COLOR_CRITICAL: 0xff3333,
  TIMER_GLOW_DIST: 15,
  // Font sizes
  WIND_SHIFT_TITLE_FONT_SIZE: 28,
  WIND_SHIFT_VALUE_FONT_SIZE: 20,
  VOID_RISE_TITLE_FONT_SIZE: 28,
  VOID_RISE_SUBTITLE_FONT_SIZE: 16,
  DIALOG_NAME_FONT_SIZE: 20,
  DIALOG_PROMPT_FONT_SIZE: 26,
  DIALOG_INSTRUCTION_FONT_SIZE: 18,
  // Confirm effects
  CONFIRM_FLASH_ALPHA: 0.12,
  CONFIRM_SHAKE: 5,
  CONFIRM_SHAKE_DECAY: 0.8,
} as const;

export const TURN_INDICATOR = {
  // === Phase 0: Impact flash + star burst (instant) ===
  STAR_ARM_COUNT: 8,              // 8-arm anime impact star
  STAR_INNER_RADIUS: 0,
  STAR_OUTER_RADIUS: 35,
  STAR_EXPAND_SPEED: 180,
  STAR_LINE_WIDTH: 3,
  STAR_LIFE: 0.22,
  FLASH_CENTER_COUNT: 3,         // bright center flash particles
  FLASH_CENTER_SCALE: 5.0,       // large — avoid candle flame
  FLASH_CENTER_LIFE: 0.1,
  DARK_FLASH_COUNT: 3,           // dark contrast flash (Sakurai: mix black into flash)
  DARK_FLASH_SCALE: 4.0,
  DARK_FLASH_LIFE: 0.12,

  // === Phase 1: Converging arrows + shockwaves (instant) ===
  ARROW_SPREAD: 0.25,            // rad between outer lines
  ARROW_INNER_RADIUS: 55,        // starts above tank (bigger than before)
  ARROW_OUTER_RADIUS: 12,        // converges toward center
  ARROW_EXPAND_SPEED: 140,
  ARROW_CENTER_WIDTH: 4,
  ARROW_SIDE_WIDTH: 2.5,
  ARROW_LIFE: 0.3,
  // Shockwave rings (3: player, dark outline, white inner)
  SHOCKWAVE_RADIUS: 70,          // bigger ring (was 50 — candle flame fix)
  SHOCKWAVE_SPEED: 240,
  SHOCKWAVE_WIDTH: 3,
  SHOCKWAVE_LIFE: 0.28,

  // === Phase 2: Upward spark fan + radial speed lines (instant) ===
  SPARK_COUNT: 16,               // more sparks (was 12)
  SPARK_SPEED_MIN: 5,
  SPARK_SPEED_MAX: 12,
  SPARK_LIFE_MIN: 0.14,
  SPARK_LIFE_MAX: 0.3,
  SPARK_SCALE_MIN: 0.3,
  SPARK_SCALE_MAX: 0.9,
  SPARK_GRAVITY: 0.08,
  SPARK_FAN_HALF_ANGLE: Math.PI / 3,  // ±60° upward cone
  // Radial speed lines (manga action lines)
  SPEED_LINE_COUNT: 8,
  SPEED_LINE_INNER: 20,
  SPEED_LINE_OUTER: 50,
  SPEED_LINE_EXPAND: 250,
  SPEED_LINE_WIDTH: 1.5,
  SPEED_LINE_LIFE: 0.15,
  // Micro-particles (Sakurai: "smaller is more")
  MICRO_COUNT: 10,

  // === Dark framing (Sakurai contrast — all phases) ===
  DARK_COUNT: 6,
  DARK_COLOR: 0x050510,
  DARK_LIFE_MIN: 0.15,
  DARK_LIFE_MAX: 0.28,
  DARK_SCALE_MIN: 1.5,
  DARK_SCALE_MAX: 3.5,

  // === Phase 3: Secondary burst (+40ms delayed) ===
  PHASE3_DELAY: 40,
  CLUSTER_COUNT: 3,              // multi-element billboard particles
  EMBER_COUNT: 6,                // ascending embers

  // === Phase 4: End flash (+80ms delayed — Sakurai Phase 4 punctuation) ===
  PHASE4_DELAY: 80,
  END_FLASH_SCALE: 3.5,
  END_FLASH_LIFE: 0.06,
  SMOKE_COUNT: 4,

  // Screen effects
  FLASH_ALPHA: 0.12,
  SHAKE_INTENSITY: 4,
  SHAKE_DECAY: 0.75,

  // Tank flash (Sakurai: character flash = state change, mix bright + dark)
  TANK_FLASH_BRIGHT: 0xffffff,
  TANK_FLASH_DARK: 0x001122,
  TANK_FLASH_DURATION: 0.25,
} as const;

// Shot Tier — screen-space panel (bottom-left) + world-space near-tank label
export const SHOT_TIER = {
  // Legacy (near-tank label Y offset)
  INDICATOR_Y_OFFSET: -72,
  INDICATOR_SPACING: 24,
  BOX_SIZE: 18,

  // Panel layout (screen-space, bottom-left)
  PANEL_X: 24,
  PANEL_Y_BOTTOM: 160,        // px from canvas bottom (above hint text)
  CARD_WIDTH: 200,
  CARD_HEIGHT: 44,
  CARD_SPACING: 6,
  CARD_CORNER_RADIUS: 4,

  // Card fonts
  TIER_FONT_SIZE: 14,
  NAME_FONT_SIZE: 15,
  COST_FONT_SIZE: 11,

  // Card visual states — selected
  SELECTED_BG_ALPHA: 0.38,
  SELECTED_BORDER_WIDTH: 3,

  // Card visual states — available
  AVAILABLE_BG_ALPHA: 0.08,
  AVAILABLE_BORDER_WIDTH: 1,
  AVAILABLE_BORDER_ALPHA: 0.4,
  AVAILABLE_TEXT_COLOR: 0xdddddd,

  // Card visual states — locked
  LOCKED_BG_ALPHA: 0.04,
  LOCKED_BORDER_ALPHA: 0.15,
  LOCKED_TEXT_COLOR: 0x555555,
  LOCKED_COST_COLOR: 0xff3333,

  // Switch animation
  FLASH_DURATION: 0.2,
  SCALE_PUNCH: 1.08,
  SCALE_SETTLE: 0.15,
  NEAR_TANK_FLASH: 0.15,
  NEAR_TANK_SCALE_POP: 1.3,

  // Near-tank label
  NEAR_TANK_ALPHA: 0.6,
  NEAR_TANK_FONT_SIZE: 18,

  // Momentum bar upgrades
  READOUT_FONT_SIZE: 14,
  READOUT_Y_ABOVE: 18,
  THRESHOLD_WIDTH: 2,
  THRESHOLD_EXTEND: 4,
  THRESHOLD_LABEL_SIZE: 9,
} as const;

// Floating player cards (bottom-center HUD)
export const PLAYER_CARD = {
  CARD_WIDTH: 200,
  CARD_HEIGHT: 100,
  CARD_GAP: 120,
  CARD_Y_BOTTOM: 40,        // px from canvas bottom
  CARD_CORNER_RADIUS: 8,
  // Background
  ACTIVE_BG_ALPHA: 0.82,
  // Border
  ACTIVE_BORDER_WIDTH: 2.5,
  INACTIVE_BORDER_WIDTH: 1.5,
  ACTIVE_GLOW_BLUR: 10,
  // Damage text
  DAMAGE_FONT_SIZE: 38,
  DAMAGE_SCALE_PUNCH: 1.45,
  DAMAGE_SHAKE_MAX: 6,
  // Label
  LABEL_FONT_SIZE: 12,
  LABEL_X: 12,
  LABEL_Y: 10,
  // Tank name
  TANK_NAME_FONT_SIZE: 11,
  TANK_NAME_X: 36,
  TANK_NAME_Y: 10,
  // Breathe
  BREATHE_SPEED: 0.9,
  BREATHE_AMPLITUDE: 2,
  // Glow decay
  GLOW_DECAY_RATE: 3,
  SCALE_SETTLE_RATE: 8,
  SHAKE_DECAY_RATE: 12,
  // Gap elements
  TIMER_FONT_SIZE: 18,
  // Shot tier pips
  PIP_RADIUS: 4,
  PIP_SPACING: 12,
  PIP_Y: 14,
  PIP_RIGHT_MARGIN: 14,
  // Charge bar (between cards)
  CHARGE_BAR_WIDTH: 300,
  CHARGE_BAR_HEIGHT: 12,
  CHARGE_BAR_CORNER_RADIUS: 6,
  CHARGE_BAR_Y_OFFSET: 18,    // px above card top
  CHARGE_BAR_BORDER_COLOR: 0x333333,
  CHARGE_BAR_COLOR_NORMAL: 0xffff00,
  CHARGE_BAR_COLOR_HIGH: 0xff8800,
  CHARGE_BAR_HIGH_THRESHOLD: 0.75,
  CHARGE_BAR_FADE_IN: 0.08,   // seconds
  CHARGE_BAR_FADE_OUT: 0.04,  // seconds
  // Turn indicator punch
  TURN_PUNCH_SCALE: 1.2,
  TURN_PUNCH_DURATION: 0.15,  // seconds
} as const;

// Announcement text popups (fighting game bold)
export const ANNOUNCE = {
  // Punch-in
  PUNCH_DURATION: 0.12,      // seconds
  START_SCALE: 1.5,           // overshoot
  // Hold + fade defaults
  HOLD_DURATION: 0.4,
  FADE_DURATION: 0.35,
  // World-space drift
  DRIFT_VY: -40,              // pixels/sec upward
  // Screen positions (fraction of canvas height)
  SCREEN_CENTER_Y: 0.38,
  SCREEN_UPPER_Y: 0.25,
} as const;

// Urgency colors — shared 3-color pattern for all timed windows
export const URGENCY = {
  WARN_THRESHOLD: 0.5,
  CRITICAL_THRESHOLD: 0.25,
  SAFE_COLOR: 0x00ffcc,
  WARN_COLOR: 0xffbb00,
  CRITICAL_COLOR: 0xff3333,
} as const;
